import React, { useRef, useEffect, useState, useMemo } from 'react';
import ReactGridLayout from 'react-grid-layout';
import { LayoutDashboard, Plus } from 'lucide-react';
import 'react-grid-layout/css/styles.css';
import DashboardWidget from './DashboardWidget';

export const ROW_HEIGHT = 80;
const COLS = 12;
const MARGIN = [10, 10];

const DOT_BG = `url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23cbd5e1' fill-opacity='0.5'/%3E%3C/svg%3E")`;

const DashboardCanvas = ({
  widgets = [],
  editMode = false,
  selectedWidgetId = null,
  onLayoutChange,
  onWidgetRemove,
  onWidgetConfigure,
  onWidgetDuplicate,
  onWidgetSelect,
  onDrillDown,
  globalFilters = {},
}) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1000);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      if (w > 0) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Memoize the react-grid-layout config. Rebuilding this array on every
  // render (e.g. on each hover/resize tick) handed RGL a new `layout`
  // reference each time, forcing avoidable layout recomputes across all
  // widgets. Recompute only when widget positions actually change.
  const layout = useMemo(
    () => widgets.map((w) => ({
      i: w.id,
      x: w.layout?.x ?? 0,
      y: w.layout?.y ?? 0,
      w: w.layout?.w ?? 6,
      h: w.layout?.h ?? 4,
      minW: w.layout?.minW ?? 2,
      minH: w.layout?.minH ?? 2,
    })),
    [widgets],
  );

  return (
    <div
      ref={containerRef}
      className="h-full overflow-auto"
      style={{ background: '#f4f5f8', backgroundImage: DOT_BG, backgroundSize: '24px 24px' }}
    >
      {widgets.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[420px] h-full text-slate-400 p-8">
          <div className="relative mb-5">
            <div className="w-20 h-20 rounded-3xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
              <LayoutDashboard className="w-10 h-10 text-indigo-200" />
            </div>
            {editMode && (
              <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-indigo-600 border-2 border-white flex items-center justify-center shadow-sm">
                <Plus className="w-4 h-4 text-white" />
              </div>
            )}
          </div>
          <p className="text-sm font-semibold text-slate-500">
            {editMode ? 'Canvas is empty' : 'No widgets yet'}
          </p>
          <p className="text-xs text-slate-400 mt-1.5 text-center max-w-xs leading-relaxed">
            {editMode
              ? 'Click any report in the left panel to add it as a widget'
              : 'Open the editor and add reports to populate this dashboard'}
          </p>
        </div>
      ) : (
        // `data-dashboard-export-root` anchors the dashboard PDF/PNG
        // export modal. This is the canonical capture target because
        // react-grid-layout sets an explicit pixel height on its inner
        // container based on widget positions — so this node's
        // scrollHeight is the full dashboard layout regardless of
        // whether widgets are scrolled into view. Capturing here
        // excludes the page header, breadcrumb and filter bar by
        // design (Salesforce-style "dashboard only" download).
        <div data-dashboard-export-root className="p-2">
          <ReactGridLayout
            layout={layout}
            cols={COLS}
            rowHeight={ROW_HEIGHT}
            margin={MARGIN}
            width={Math.max(200, containerWidth - 16)}
            isDraggable={editMode}
            isResizable={editMode}
            draggableHandle=".widget-drag-handle"
            onLayoutChange={(newLayout) => onLayoutChange && onLayoutChange(newLayout)}
            resizeHandles={editMode ? ['se', 'sw', 'ne', 'nw'] : []}
            compactType="vertical"
            preventCollision={false}
          >
            {widgets.map((widget) => (
              <div
                key={widget.id}
                className={`transition-all duration-150 rounded-xl ${
                  editMode && selectedWidgetId === widget.id
                    ? 'ring-2 ring-indigo-500 ring-offset-2 shadow-lg'
                    : ''
                }`}
                onClick={() => editMode && onWidgetSelect && onWidgetSelect(widget.id)}
              >
                <DashboardWidget
                  widget={widget}
                  editMode={editMode}
                  onRemove={onWidgetRemove}
                  onConfigure={onWidgetConfigure}
                  onDuplicate={onWidgetDuplicate}
                  onDrillDown={onDrillDown}
                  globalFilters={globalFilters}
                />
              </div>
            ))}
          </ReactGridLayout>
        </div>
      )}
    </div>
  );
};

export default DashboardCanvas;
